import { useDeleteMutation, WorkspaceRoutine } from '@gripp/shared-logic';
import {
  Colors,
  ConfirmationOverlay,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-native-paper';

type DeleteRoutineMenuItemProps = {
  onSelected: () => void;
  workspaceRoutine: WorkspaceRoutine;
};

// useFetcher (the transport behind useDeleteMutation) throws a plain
// Error whose message is the GraphQL error message (json.errors[0].message),
// so the human-readable text is simply error.message.
const extractErrorMessage = (error: unknown): string | undefined =>
  error instanceof Error ? error.message : undefined;

export const DeleteRoutineButton = (props: DeleteRoutineMenuItemProps) => {
  const { t } = useTranslation();
  const { setColumnOverlay } = useSlideLayout();
  const { mutateAsync, isPending } = useDeleteMutation({
    modelName: 'WorkspaceRoutine',
    query: DELETE_ROUTINE,
  });

  const hideOverlay = () => setColumnOverlay(null);

  const handleDeleteConfirm = async () => {
    if (isPending) return;
    try {
      await mutateAsync({ input: { id: props.workspaceRoutine.id } });
      hideOverlay();
      props.onSelected();
    } catch (error) {
      // The API blocks deleting a routine that is still nested inside a
      // workflow (ROUTINE_IN_USE_BY_WORKFLOW) and returns a message naming the
      // workflows. Surface it instead of silently leaving the confirm overlay
      // open with no feedback.
      setColumnOverlay(
        <ConfirmationOverlay
          title={t('routines.menu.delete.blockedTitle')}
          subtitle={
            extractErrorMessage(error) ??
            t('routines.menu.delete.blockedGeneric')
          }
          confirmLabel={t('common.ok')}
          confirmState="active"
          onConfirm={hideOverlay}
          cancelLabel={t('common.close')}
          onCancel={hideOverlay}
        />
      );
    }
  };

  const handleDeletePress = () => {
    setColumnOverlay(
      <ConfirmationOverlay
        title={t('common.confirmDelete', { name: props.workspaceRoutine.name })}
        subtitle={t('common.deleteWarning')}
        confirmLabel={t('common.delete')}
        onConfirm={handleDeleteConfirm}
        cancelLabel={t('routines.menu.delete.cancel')}
        onCancel={hideOverlay}
      />
    );
  };

  return (
    <Button
      mode="text"
      style={{ width: 152 }}
      labelStyle={{
        fontSize: 16,
      }}
      textColor={Colors.red}
      onPress={handleDeletePress}
    >
      Delete Routine
    </Button>
  );
};

const DELETE_ROUTINE = `
  mutation DeleteWorkspaceRoutine($input: MutateWorkspaceRoutineInput!) {
    deleteWorkspaceRoutine(input: $input) {
      id
    }
  }
`;
